import { useState } from 'react';
import { Navigate, createBrowserRouter, RouterProvider, Outlet, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ConfirmDialog from './components/ConfirmDialog';
import Login from './pages/Login';
import Affiliates from './pages/Affiliates';
import Users from './pages/Users';
import Transactions from './pages/Transactions';
import Deposits from './pages/Deposits';
import SettingsDialog from './components/SettingsDialog';

function ProtectedLayout() {
  const { token, loading, logout } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  if (loading) {
    return <div className="text-center mt-5"><div className="spinner-border" /></div>;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="d-flex flex-column vh-100">
      <Header onSettings={() => setShowSettings(true)} onLogout={() => setShowLogoutConfirm(true)} />
      <main className="flex-grow-1 overflow-auto">
        <PageTabs />
        <Outlet />
      </main>
      <Footer />

      {showSettings && (
        <SettingsDialog onClose={() => setShowSettings(false)} />
      )}

      {showLogoutConfirm && (
        <ConfirmDialog
          title="Log Out"
          message="Are you sure you want to log out?"
          confirmLabel="Log Out"
          onConfirm={() => { setShowLogoutConfirm(false); logout(); }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </div>
  );
}

function Header({ onSettings, onLogout }: { onSettings: () => void; onLogout: () => void }) {
  return (
    <nav className="navbar navbar-dark bg-dark flex-shrink-0 py-1">
      <div className="container-fluid px-3">
        <span className="navbar-brand mb-0 small">Polyreader Admin</span>
        <div className="d-flex gap-2">
          <button onClick={onSettings} className="btn btn-outline-light btn-sm">Settings</button>
          <button onClick={onLogout} className="btn btn-outline-light btn-sm">Logout</button>
        </div>
      </div>
    </nav>
  );
}

function PageTabs() {
  const location = useLocation();
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const tabs: { path: string; label: string }[] = [
    { path: '/affiliates', label: 'Affiliates' },
    { path: '/users', label: 'Users' },
    { path: '/transactions', label: 'Transactions' },
    { path: '/deposits', label: 'Deposits' },
  ];

  return (
    <ul className="nav nav-tabs px-3 pt-3">
      {tabs.map(t => (
        <li className="nav-item" key={t.path}>
          <Link to={t.path} className={`nav-link ${isActive(t.path) ? 'active' : ''}`}>{t.label}</Link>
        </li>
      ))}
    </ul>
  );
}

function Footer() {
  return (
    <footer className="bg-dark text-light text-center flex-shrink-0 py-1">
      <small className="text-light text-opacity-50">&copy; Woldo 2026</small>
    </footer>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    element: <ProtectedLayout />,
    children: [
      { path: '/', element: <Navigate to="/affiliates" replace /> },
      { path: '/affiliates', element: <Affiliates /> },
      { path: '/users', element: <Users /> },
      { path: '/transactions', element: <Transactions /> },
      { path: '/deposits', element: <Deposits /> },
    ],
  },
]);

export default function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
